import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Clock,
  Lock,
  MessageSquare,
  Send,
  UserCheck,
} from "lucide-react";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { staff as staffApi, tickets } from "@/api/endpoints";
import type { TicketStatus } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { Field } from "@/components/PageHeader";
import { Badge, toneFor } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ErrorState, FormError, Spinner } from "@/components/ui/feedback";
import { Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { cn, formatDateTime, humanize, initials, relativeTime } from "@/lib/utils";

/**
 * Mirrors the transitions the service allows. Keeping this in sync means the
 * agent never gets a 409 they could have been shown up front — the server is
 * still the authority.
 */
const ALLOWED_TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  OPEN: ["IN_PROGRESS", "PENDING_CUSTOMER", "RESOLVED", "CLOSED"],
  IN_PROGRESS: ["PENDING_CUSTOMER", "RESOLVED", "CLOSED"],
  PENDING_CUSTOMER: ["IN_PROGRESS", "RESOLVED", "CLOSED"],
  RESOLVED: ["CLOSED", "IN_PROGRESS"],
  CLOSED: [],
};

export default function TicketDetail() {
  const { ticketId = "" } = useParams();
  const { can, me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [reply, setReply] = React.useState("");
  const [internal, setInternal] = React.useState(false);
  const [replyError, setReplyError] = React.useState<string | null>(null);
  const [statusTarget, setStatusTarget] = React.useState<TicketStatus | null>(null);

  const query = useQuery({
    queryKey: ["ticket", ticketId],
    queryFn: () => tickets.get(ticketId),
  });

  const staffList = useQuery({
    queryKey: ["staff"],
    queryFn: staffApi.list,
    enabled: can("staff.manage"),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["ticket", ticketId] });
    void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    void queryClient.invalidateQueries({ queryKey: ["ticket-stats"] });
  }

  const postReply = useMutation({
    mutationFn: () => tickets.reply(ticketId, reply.trim(), internal),
    onSuccess: () => {
      setReply("");
      setReplyError(null);
      invalidate();
    },
    onError: (err) =>
      setReplyError(
        err instanceof ApiError ? err.message : "Could not post the message.",
      ),
  });

  const assign = useMutation({
    mutationFn: (assigneeId: string | null) => tickets.assign(ticketId, assigneeId),
    onSuccess: () => {
      toast.success("Assignment updated");
      invalidate();
    },
    onError: (err) =>
      toast.error(
        "Could not reassign",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="size-6 text-muted-foreground" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  }

  const ticket = query.data!;
  const closed = ticket.status === "CLOSED";
  const canWrite = can("ticket.write");
  const transitions = ALLOWED_TRANSITIONS[ticket.status];

  return (
    <>
      <Link
        to="/tickets"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to tickets
      </Link>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="tabular text-sm text-muted-foreground">
              {ticket.reference}
            </span>
            <Badge variant={toneFor(ticket.status)}>{humanize(ticket.status)}</Badge>
            <Badge variant={toneFor(ticket.priority)}>
              {humanize(ticket.priority)}
            </Badge>
            {ticket.is_sla_breached && (
              <Badge variant="critical">
                <Clock className="size-3" />
                Past SLA
              </Badge>
            )}
          </div>
          <h1 className="mt-1.5 text-xl font-semibold tracking-tight">
            {ticket.subject}
          </h1>
        </div>

        {canWrite && transitions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {transitions.map((next) => (
              <Button
                key={next}
                variant={next === "CLOSED" ? "outline" : "default"}
                size="sm"
                onClick={() => setStatusTarget(next)}
              >
                {next === "IN_PROGRESS" && ticket.status === "RESOLVED"
                  ? "Reopen"
                  : humanize(next)}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Conversation */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Conversation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {ticket.messages?.length ? (
                ticket.messages.map((m) => (
                  <div
                    key={m.id}
                    className={cn(
                      "rounded-lg border p-3",
                      m.is_internal
                        ? "border-dashed border-[color-mix(in_srgb,var(--status-warning)_45%,transparent)] bg-[color-mix(in_srgb,var(--status-warning)_8%,transparent)]"
                        : "border-border bg-card",
                    )}
                  >
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <span className="flex size-6 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold text-secondary-foreground">
                        {m.author_type === "STAFF" ? initials(me?.full_name) : "C"}
                      </span>
                      <span className="text-xs font-medium">
                        {m.author_type === "STAFF"
                          ? "Support"
                          : m.author_type === "USER"
                            ? "Customer"
                            : "System"}
                      </span>
                      {m.is_internal && (
                        <Badge variant="warning">
                          <Lock className="size-3" />
                          Internal note
                        </Badge>
                      )}
                      <span className="ml-auto text-[11px] text-muted-foreground">
                        {formatDateTime(m.created_at)}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-sm">
                      {m.body}
                    </p>
                  </div>
                ))
              ) : (
                <p className="py-6 text-center text-xs text-muted-foreground">
                  No messages yet.
                </p>
              )}

              {canWrite && !closed && (
                <form
                  className="space-y-2 border-t border-border pt-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!reply.trim()) return;
                    postReply.mutate();
                  }}
                >
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={3}
                    placeholder={
                      internal
                        ? "Internal note — the customer will not see this."
                        : "Reply to the customer…"
                    }
                  />
                  <FormError message={replyError} />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={internal}
                        onChange={(e) => setInternal(e.target.checked)}
                        className="size-3.5 accent-[hsl(var(--primary))]"
                      />
                      Internal note (does not start the first-response clock)
                    </label>
                    <Button
                      type="submit"
                      size="sm"
                      disabled={postReply.isPending || !reply.trim()}
                    >
                      {postReply.isPending ? <Spinner /> : <Send />}
                      {internal ? "Add note" : "Send reply"}
                    </Button>
                  </div>
                </form>
              )}

              {closed && (
                <p className="border-t border-border pt-3 text-center text-xs text-muted-foreground">
                  This ticket is closed. Raise a new one to continue.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3">
                <Field label="Requester">
                  <span className="tabular">{ticket.requester_contact ?? "—"}</span>
                </Field>
                <Field label="Channel">{humanize(ticket.channel)}</Field>
                <Field label="Created">{formatDateTime(ticket.created_at)}</Field>
                <Field label="SLA target">
                  {ticket.sla_due_at ? (
                    <span
                      style={{
                        color: ticket.is_sla_breached
                          ? "var(--status-critical)"
                          : undefined,
                      }}
                    >
                      {formatDateTime(ticket.sla_due_at)} (
                      {relativeTime(ticket.sla_due_at)})
                    </span>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="First response">
                  {formatDateTime(ticket.first_response_at)}
                </Field>
                {ticket.resolution && (
                  <Field label="Resolution">{ticket.resolution}</Field>
                )}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Assignment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {can("ticket.assign") ? (
                staffList.data ? (
                  <Select
                    value={ticket.assignee_staff_id ?? ""}
                    onValueChange={(v) => assign.mutate(v || null)}
                    options={staffList.data.map((s) => ({
                      label: s.full_name,
                      value: s.id,
                    }))}
                    placeholder="Unassigned"
                    aria-label="Assign ticket"
                  />
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={assign.isPending}
                    onClick={() => assign.mutate(me?.id ?? null)}
                  >
                    <UserCheck />
                    Assign to me
                  </Button>
                )
              ) : (
                <p className="text-xs text-muted-foreground">
                  {ticket.assignee_staff_id
                    ? "Assigned to a colleague."
                    : "Unassigned. You need the ticket.assign permission to change this."}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {statusTarget && (
        <StatusDialog
          ticketId={ticketId}
          target={statusTarget}
          hasResolution={Boolean(ticket.resolution)}
          onClose={() => setStatusTarget(null)}
          onDone={invalidate}
        />
      )}
    </>
  );
}

function StatusDialog({
  ticketId,
  target,
  hasResolution,
  onClose,
  onDone,
}: {
  ticketId: string;
  target: TicketStatus;
  hasResolution: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [resolution, setResolution] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  // The server requires a resolution to resolve or close; check it here so the
  // agent isn't bounced by a 422 after typing nothing.
  const needsResolution =
    (target === "RESOLVED" || target === "CLOSED") && !hasResolution;

  const mutation = useMutation({
    mutationFn: () => tickets.setStatus(ticketId, target, resolution.trim() || undefined),
    onSuccess: () => {
      toast.success(`Ticket moved to ${humanize(target)}`);
      onDone();
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not change the status."),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move to {humanize(target)}</DialogTitle>
          <DialogDescription>
            {target === "CLOSED"
              ? "Closed tickets cannot be reopened — a new ticket is needed instead."
              : "This change is recorded in the audit log."}
          </DialogDescription>
        </DialogHeader>

        {needsResolution && (
          <div className="space-y-1.5">
            <Label htmlFor="resolution">Resolution</Label>
            <Textarea
              id="resolution"
              rows={3}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="What was done to resolve this?"
            />
          </div>
        )}

        <FormError message={error} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={
              mutation.isPending || (needsResolution && !resolution.trim())
            }
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            {mutation.isPending ? <Spinner /> : <MessageSquare />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
